import { buildHeaders as montarCabecalhos, buildUrl as montarUrl } from '@locaveri/shared'
import type { OpcoesDeCabecalhos } from '@locaveri/shared'
import { z } from 'zod'

import { env } from './env'

/**
 * Primitivas HTTP do painel — SEM nenhuma noção de sessão.
 *
 * Este módulo existe separado de `api.ts` por causa da renovação de token: o
 * cliente autenticado (`api.ts`) precisa pedir um access token novo, e quem
 * renova (`sessao.ts`) precisa montar URL e cabeçalho. Se os três morassem no
 * mesmo arquivo — ou se `http` conhecesse a sessão — teríamos um ciclo de
 * importação e, pior, a chamada de renovação passaria pelo interceptor que
 * dispara a própria renovação.
 *
 * A dependência é de mão única:  http.ts  ←  sessao.ts  ←  api.ts
 *
 * O contrato de erro da API é sempre o mesmo envelope:
 *
 *     { "error": { "code": "...", "message": "...", "details": ..., "traceId": "..." } }
 */

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** Envelope de erro devolvido pela API. */
export const apiErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    details: z.unknown().optional(),
    traceId: z.string().optional(),
  }),
})

export type ApiErrorEnvelope = z.infer<typeof apiErrorEnvelopeSchema>

export interface ApiErrorInit {
  /** HTTP status. `0` quando a requisição nem chegou ao servidor. */
  readonly status: number
  readonly code: string
  readonly message: string
  readonly details: unknown
  readonly traceId: string | undefined
}

export class ApiError extends Error {
  override readonly name: string = 'ApiError'
  readonly status: number
  readonly code: string
  readonly details: unknown
  readonly traceId: string | undefined

  constructor(init: ApiErrorInit) {
    super(init.message)
    this.status = init.status
    this.code = init.code
    this.details = init.details
    this.traceId = init.traceId
  }

  /** `true` quando não adianta repetir: o servidor recusou o pedido. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500
  }

  /** `true` quando a requisição não chegou ao servidor (offline, DNS, CORS). */
  get isNetworkError(): boolean {
    return this.status === 0
  }

  static is(value: unknown): value is ApiError {
    return value instanceof ApiError
  }
}

/** Códigos que o próprio cliente emite quando o erro não veio da API. */
export const CLIENT_ERROR_CODES = {
  /** A requisição não chegou ao servidor. */
  network: 'NETWORK_ERROR',
  /** O servidor respondeu, mas fora do contrato declarado pelo schema. */
  invalidResponse: 'INVALID_RESPONSE',
} as const

/** Mensagens de fallback em pt-BR, por faixa de status. */
export function fallbackMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Requisição inválida. Revise os dados enviados.'
    case 401:
      return 'Sua sessão expirou. Entre novamente para continuar.'
    case 403:
      return 'Você não tem permissão para esta ação.'
    case 404:
      return 'Não encontramos o que você procura.'
    case 409:
      return 'Este registro foi alterado por outra pessoa. Recarregue e tente de novo.'
    case 422:
      return 'Alguns campos não passaram na validação.'
    case 429:
      return 'Muitas requisições em pouco tempo. Aguarde alguns segundos.'
    default:
      return status >= 500
        ? 'O servidor não conseguiu concluir a operação. Tente novamente em instantes.'
        : 'Não foi possível concluir a operação.'
  }
}

/**
 * URL e cabeçalhos: a MONTAGEM mora no `@locaveri/shared` desde a Fase 6, porque
 * o site público faz exatamente as mesmas duas coisas antes de cada chamada.
 * O que fica aqui é só o que é do PAINEL — de onde vêm a base da API e o slug
 * do tenant, que no site vêm de outras variáveis de ambiente.
 */
export function buildUrl(path: string): string {
  return montarUrl(env.VITE_API_BASE_URL, path)
}

/** As opções de cabeçalho do painel: as do shared, menos o tenant (que é fixo). */
export type CabecalhosOptions = Omit<OpcoesDeCabecalhos, 'tenantSlug'>

/**
 * O pedido de sessão em cookie `httpOnly` — em TODA requisição do painel.
 *
 * Só as rotas que emitem sessão (login, 2FA, refresh, login social) olham para
 * ele: nelas a API grava o refresh no cookie e devolve o campo VAZIO no corpo,
 * que é o que `definirSessao` já trata como "o cookie está valendo". Mandar em
 * toda requisição é mais simples que lembrar de mandar nas certas — e esquecer
 * numa delas era exatamente o tipo de falha que deixou o cookie sem ninguém
 * que o gravasse desde a Fase 1.
 */
export const CABECALHO_DE_SESSAO_EM_COOKIE = 'x-sessao-em-cookie'

export function buildHeaders(options: CabecalhosOptions): Headers {
  const cabecalhos = new Headers(
    montarCabecalhos({ ...options, tenantSlug: env.VITE_DEFAULT_TENANT_SLUG }),
  )
  cabecalhos.set(CABECALHO_DE_SESSAO_EM_COOKIE, '1')
  return cabecalhos
}

/** Lê o corpo uma única vez. Devolve `undefined` para 204 e afins. */
export async function readBody(response: Response): Promise<unknown> {
  const text = await response.text()

  if (text.length === 0) {
    return undefined
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

export function toApiError(status: number, payload: unknown): ApiError {
  const envelope = apiErrorEnvelopeSchema.safeParse(payload)

  if (envelope.success) {
    const { code, message, details, traceId } = envelope.data.error
    return new ApiError({ status, code, message, details, traceId })
  }

  return new ApiError({
    status,
    code: `HTTP_${String(status)}`,
    message: fallbackMessage(status),
    details: payload,
    traceId: undefined,
  })
}

/** Erro de rede (a requisição não chegou ao servidor). */
export function toNetworkError(cause: unknown): ApiError {
  return new ApiError({
    status: 0,
    code: CLIENT_ERROR_CODES.network,
    message: 'Não foi possível falar com o servidor. Verifique sua conexão.',
    details: cause,
    traceId: undefined,
  })
}

/**
 * Mensagem pronta para a UI a partir de qualquer erro. Nunca devolve string
 * vazia — o requisito de "nunca tela branca" vale também para o texto do erro.
 */
export function errorMessage(error: unknown): string {
  if (ApiError.is(error)) {
    return error.message
  }

  if (error instanceof Error && error.message.length > 0) {
    return error.message
  }

  return 'Algo saiu errado. Tente novamente.'
}
